<script setup lang="ts">
import type { components } from '@/api/clarity'
import { EntryIcon } from '@/components/EntryIcon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  journalEntryTypes,
  personJournalEntryTypes,
  type JournalEntryFeedback,
  type JournalEntryType,
} from '@/db/journal'
import { safeDateTimeFormat } from '@/lib/intl'
import {
  getLocalTimeZone,
  parseDate,
  startOfWeek,
  today,
  type CalendarDate,
  type DateTimeDuration,
  type DateValue,
} from '@internationalized/date'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    activityData: components['schemas']['ActivityContainer']
    personOnly?: boolean
    weeksToShow?: number
    expectedDuration?: DateTimeDuration
  }>(),
  {
    personOnly: false,
    weeksToShow: 13,
    expectedDuration: undefined,
  },
)

const entryTypes = computed(() => (props.personOnly ? personJournalEntryTypes() : journalEntryTypes()))

// Format for displaying week ranges in tooltips and last entry dates
const monthDayFormat = safeDateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
})

// Generate array of week start dates, from oldest to newest (left to right)
const weeks = computed(() => {
  const result: CalendarDate[] = []
  const currentWeekStart = startOfWeek(today(getLocalTimeZone()), monthDayFormat.resolvedOptions().locale)

  for (let i = props.weeksToShow - 1; i >= 0; i--) {
    result.push(currentWeekStart.subtract({ weeks: i }))
  }

  return result
})

function formatWeekRange(weekStart: CalendarDate): string {
  const weekEnd = weekStart.add({ days: 6 })

  return monthDayFormat.formatRange(weekStart.toDate(getLocalTimeZone()), weekEnd.toDate(getLocalTimeZone()))
}

function formatLastEntryDate(date: DateValue | undefined): string {
  if (!date) return 'None'
  return monthDayFormat.format(date.toDate(getLocalTimeZone()))
}

const monthLabelFormat = safeDateTimeFormat(undefined, { month: 'short' })

// Get month label for a week (only if it's the first week of a month or first week shown)
function getMonthLabel(weekStart: CalendarDate, index: number): string | null {
  if (index === 0) {
    // if the next week is a new month, don't show the label
    const nextWeek = weeks.value[index + 1]
    if (nextWeek && weekStart.month !== nextWeek.month) {
      return null
    }
    return monthLabelFormat.format(weekStart.toDate(getLocalTimeZone()))
  }

  const prevWeek = weeks.value[index - 1]
  if (prevWeek && weekStart.month !== prevWeek.month) {
    return monthLabelFormat.format(weekStart.toDate(getLocalTimeZone()))
  }

  return null
}

type FeedbackSentiment = JournalEntryFeedback['content']['category']
type SentimentWrapper = { count: number; sentiment?: FeedbackSentiment }

// Compute entry counts per week per type
const weekData = computed(() => {
  const data = new Map<number, Map<JournalEntryType, SentimentWrapper>>()

  weeks.value.forEach((weekStart, weekIndex) => {
    const weekKey = weekStart.toString() // Calendar date format: YYYY-MM-DD
    const typeMap = new Map<JournalEntryType, SentimentWrapper>()

    for (const type of entryTypes.value) {
      const typeSummary = props.activityData.total[weekKey]?.[type] ?? { count: 0 }
      typeMap.set(type, typeSummary)
    }

    data.set(weekIndex, typeMap)
  })

  return data
})

// Get cell background class based on entry type, count, and sentiment
function getCellClass(type: JournalEntryType, weekIndex: number): string {
  const typeData = weekData.value.get(weekIndex)?.get(type)
  const count = typeData?.count ?? 0

  if (count === 0) {
    return 'bg-foreground/5'
  }

  if (type === 'feedback') {
    const sentiment = typeData?.sentiment ?? 'mixed'
    switch (sentiment) {
      case 'positive':
        return count >= 2 ? 'bg-feedback-positive brightness-75' : 'bg-feedback-positive'
      case 'critical':
        return count >= 2 ? 'bg-feedback-critical brightness-75' : 'bg-feedback-critical'
      case 'mixed':
      default:
        return count >= 2 ? 'bg-feedback-mixed brightness-75' : 'bg-feedback-mixed'
    }
  }

  switch (type) {
    case 'event':
      return 'bg-muted'
    case 'goals':
      return count >= 2 ? 'bg-goals brightness-75' : 'bg-goals'
    case '1-1':
      return count >= 2 ? 'bg-oneone brightness-75' : 'bg-oneone'
    case 'quick':
      return count >= 2 ? 'bg-quicknote brightness-75' : 'bg-quicknote'
    case 'report':
      return count >= 2 ? 'bg-report brightness-75' : 'bg-report'
    default:
      return 'bg-muted'
  }
}

function getEntryCount(type: JournalEntryType, weekIndex: number): number {
  return weekData.value.get(weekIndex)?.get(type)?.count ?? 0
}

function getTooltipText(type: JournalEntryType, weekIndex: number): string {
  const count = getEntryCount(type, weekIndex)
  const weekStart = weeks.value[weekIndex]
  if (!weekStart) return ''
  const range = formatWeekRange(weekStart)

  if (count === 0) {
    return `No ${entryTypeLabels[type].toLowerCase()} entries, ${range}`
  }

  const entryWord = count === 1 ? 'entry' : 'entries'
  return `${count} ${entryTypeLabels[type].toLowerCase()} ${entryWord}, ${range}`
}

const entryTypeLabels: Record<JournalEntryType, string> = {
  quick: 'Note',
  event: 'Event',
  feedback: 'Feedback',
  goals: 'Goals',
  '1-1': '1:1',
  report: 'Report',
}

const lastEntryDateByType = computed(() => {
  return Object.entries(props.activityData.latest).reduce(
    (acc, [type, dateStr]) => {
      acc[type as JournalEntryType] = parseDate(dateStr)
      return acc
    },
    {} as Record<JournalEntryType, DateValue>,
  )
})

function getEntryDateClass(date: DateValue | undefined, type: JournalEntryType): string {
  const defaultClass = 'text-muted-foreground'
  if (!date || !props.expectedDuration) return defaultClass
  if (type !== '1-1' && type !== 'feedback') return defaultClass // only consider 1-1s and feedback for overdue status
  const now = today(getLocalTimeZone())
  const compare = date.add(props.expectedDuration).compare(now)
  if (compare < 0) {
    return 'text-red-400'
  } else if (compare > 0) {
    return 'text-green-500'
  }
  return defaultClass
}

function getEntryColor(type: JournalEntryType): string {
  switch (type) {
    case 'event':
      return 'fill-muted-foreground' // events are gray and don't have a corresponding cell color, so add a light background to the icon for visibility
    case 'feedback':
      return 'fill-feedback-positive' // just use positive color for the icon, since it will be shown alongside the sentiment-specific cell colors
    case 'goals':
      return 'fill-goals'
    case '1-1':
      return 'fill-oneone'
    case 'quick':
      return 'fill-quicknote'
    case 'report':
      return 'fill-report'
    default:
      return 'fill-foreground'
  }
}
</script>

<template>
  <TooltipProvider :delay-duration="200">
    <div class="flex w-full">
      <!-- Fixed labels column -->
      <div class="w-fit shrink-0 flex flex-col gap-1 p-0.5 pr-4">
        <!-- Spacer for month labels row -->
        <div class="h-5"></div>
        <!-- Type labels -->
        <div
          v-for="type in entryTypes"
          :key="`label-${type}`"
          class="h-3 flex items-center gap-1 text-xs text-muted-foreground"
        >
          <EntryIcon :type="type" :size="14" class="shrink-0 stroke-1" :class="getEntryColor(type)" />
          <span>{{ entryTypeLabels[type] }}</span>
        </div>
      </div>

      <!-- Scrollable weeks area - direction:rtl makes scroll start from right (most recent) -->
      <div class="overflow-x-auto direction-rtl dark:scheme-dark">
        <div class="inline-flex flex-col gap-1 min-w-max direction-ltr p-0.5">
          <!-- Month labels row -->
          <div class="flex gap-0.5 h-5 items-end text-xs text-muted-foreground">
            <div v-for="(week, index) in weeks" :key="week.toString()" class="w-3">
              {{ getMonthLabel(week, index) ?? '' }}
            </div>
          </div>

          <!-- Week cells for each type -->
          <div v-for="type in entryTypes" :key="`cells-${type}`" class="flex gap-0.5">
            <Tooltip v-for="(week, weekIndex) in weeks" :key="week.toString()">
              <TooltipTrigger as-child>
                <div
                  :class="[
                    getCellClass(type, weekIndex),
                    'size-3 rounded-full cursor-default transition-colors hover:ring-1 hover:ring-foreground/20',
                  ]"
                ></div>
              </TooltipTrigger>
              <TooltipContent>
                <p class="text-sm">{{ getTooltipText(type, weekIndex) }}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      <!-- Last entry date column -->
      <div class="w-fit shrink-0 flex flex-col gap-1 p-0.5 pl-4">
        <div class="h-5 flex items-end">
          <span class="text-xs text-muted-foreground">Latest</span>
        </div>
        <div v-for="type in entryTypes" :key="`label-${type}`" class="h-3 flex items-center text-xs">
          <span :class="getEntryDateClass(lastEntryDateByType[type], type)">{{
            formatLastEntryDate(lastEntryDateByType[type])
          }}</span>
        </div>
      </div>
    </div>
  </TooltipProvider>
</template>
